/*
 * Whether a workspace's inbox is still filling (LEA-483, docs/2026-11-02-lea-483-484-import-owner-and-progress-plan.md,
 * D-4). Filling is campaign discovery, then each enabled campaign's 90-day reply backfill and the
 * drain of what it found; the message index fills only the More folders and does not count. Bison
 * gives no total inside a campaign, so the only honest count is campaigns. Pure: the repository
 * reads the tables, the routes and the web share this shape.
 */
import { z } from "zod";

export const ImportStateSchema = z.discriminatedUnion("state", [
  /** No campaign bound yet and discovery has never succeeded. */
  z.strictObject({ state: z.literal("finding") }),
  /** Enabled campaigns whose 90 days are in (`done`) of all enabled campaigns (`total`). */
  z
    .strictObject({
      state: z.literal("importing"),
      done: z.number().int().nonnegative(),
      total: z.number().int().positive(),
    })
    .refine((value) => value.done < value.total, { message: "importing_done_below_total" }),
  /** Nothing left to import, including a workspace being or already offboarded. */
  z.strictObject({ state: z.literal("complete") }),
]);
export type ImportState = z.infer<typeof ImportStateSchema>;

export const InboxImportStatusSchema = z.strictObject({ import: ImportStateSchema });

/** Whole percent of campaigns done, 0 to 99 while importing: a full bar means finished. */
export function importPercent(state: ImportState): number {
  if (state.state !== "importing") return state.state === "complete" ? 100 : 0;
  return Math.min(99, Math.floor((state.done / state.total) * 100));
}

/** The last quarter passed (0, 25, 50 or 75), for one polite announcement per quarter. */
export function importMilestone(state: ImportState): 0 | 25 | 50 | 75 {
  if (state.state !== "importing") return 0;
  const percent = importPercent(state);
  if (percent >= 75) return 75;
  if (percent >= 50) return 50;
  return percent >= 25 ? 25 : 0;
}
